/**
 * Copyright 2016 The AMP HTML Authors. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS-IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Converts a 8-bit bytes array into a string
 */
export function bytesToString(bytes: Uint8Array): string {
  // Intentionally avoids String.fromCharCode.apply so we don't suffer a
  // stack overflow. #10495, https://jsperf.com/bytesToString-2
  const array = new Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) {
    array[i] = String.fromCharCode(bytes[i]);
  }
  return array.join('');
}

/**
 * Converts a string which holds 8-bit code points, such as the result of atob,
 * into a Uint8Array with the corresponding bytes.
 * If you have a string of characters, you probably want to be using utf8Encode.
 */
export function stringToBytes(str: string): Uint8Array {
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i += 1) {
    const charCode = str.charCodeAt(i);
    bytes[i] = charCode;
  }
  return bytes;
}

/**
 * Interpret a byte array as a UTF-8 string.
 */
export function utf8Decode(bytes: ArrayBuffer): string {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder('utf-8').decode(bytes);
  }
  const asciiString = bytesToString(new Uint8Array(bytes));
  return decodeURIComponent(escape(asciiString));
}

/**
 * Converts a 4-item byte array to an unsigned integer.
 * Assumes bytes are big endian.
 */
export function bytesToUInt32(bytes: Uint8Array): number {
  if (bytes.length !== 4) {
    throw new Error('Received byte array with length != 4');
  }
  const val =
    // eslint-disable-next-line no-bitwise
    ((bytes[0] & 0xff) << 24) |
    // eslint-disable-next-line no-bitwise
    ((bytes[1] & 0xff) << 16) |
    // eslint-disable-next-line no-bitwise
    ((bytes[2] & 0xff) << 8) |
    // eslint-disable-next-line no-bitwise
    (bytes[3] & 0xff);
  // Convert to unsigned.
  // eslint-disable-next-line no-bitwise
  return val >>> 0;
}

/**
 * Generate a random bytes array with specific length using
 * win.crypto.getRandomValues. Return null if it is not available.
 */
export function getCryptoRandomBytesArray(win: Window, length: number): Uint8Array {
  const cryptoLib = win.crypto;

  // Widely available in browsers we support:
  // http://caniuse.com/#search=getRandomValues
  const uint8array = new Uint8Array(length);
  cryptoLib.getRandomValues(uint8array);
  return uint8array;
}
